import { IconButton } from '@nike/nike-design-system-components';
import { func, bool } from 'prop-types';
import { Close } from '@nike/nike-design-system-icons';
import React, { useContext, useEffect, useRef } from 'react';
import { useFocusManager } from 'react-aria';
import useIdentity from '../../utils/hooks/identityHook';
import {
  BODEGA_DEFAULTS,
  CURATED_LANDING_IN_SCOPE_PATHS,
  CURATED_LANDING_NAV_LOCATION,
  DATA_TYPES,
  MENU_TYPES,
} from '../../constants';
import useMenuData from '../../utils/hooks/cmsDataHook';
import useBodega from '../../utils/hooks/bodegaHook';
import useExperiment, { getExperiment } from '../../utils/hooks/experimentHook';
import { getMobileMenuItems } from '../../utils/menuUtils';
import {
  BRAND_MENU_WRAPPER_CLASS,
  CLOSE_BTN_WRAPPER_CLASS,
  MAIN_MENU_WRAPPER,
  MOBILE_MENU_CLOSE_BTN,
} from '../MobileMenu/styles';
import MobileAccountMenu from './mobileAccountMenuButton';
import MobileShoppingMenuLink from './mobileShoppingMenuLink';
import BrandMenu from '../BrandMenu';
import MobileUtilityMenu from './mobileUtilityMenu';
import { FocusContext, getMainMenuLink } from '../MobileMenu';

/**
 * The first menu that is rendered by MobileMenu
 * @param {object} props
 * @param {func} props.closeMobileMenu
 * @param {bool} props.menuIsSelected only one base menu wil ever be rendered but many
 * sub menus can be rendered at once. We don't want focus to return to this menu until
 * it has been reselected by closing the other menus. this bool let's us know if we
 * should set focus
 * @returns {React.ReactElement}
 */
const BaseMobileMenu = ({ closeMobileMenu, menuIsSelected, isMobileNavDrawerOpen }) => {
  const { openSubMenu, curFocusRef } = useContext(FocusContext);
  const focusManager = useFocusManager();
  const wrapperRef = useRef(null);
  const keyDownHandler = ({ nativeEvent }) => {
    switch (nativeEvent.which) {
      case 32: // SPACE
        nativeEvent.preventDefault(); // Prevent scroll when using space bar.

        closeMobileMenu();
        break;

      default:
        break;
    }
  };
  const { isLoggedIn } = useIdentity();
  const mainMenu = useMenuData(DATA_TYPES.HEADER, MENU_TYPES.MAIN_MENU);
  const allProductMsg = useBodega('nav_title_all_menuitem', BODEGA_DEFAULTS.ALL);
  const closeMenuLabel = useBodega('nav_close_menu_aria_label', BODEGA_DEFAULTS.CLOSE_MENU);
  const mobileMainMenuItems = getMobileMenuItems(mainMenu);
  const experimentData = useExperiment([{ name: CURATED_LANDING_NAV_LOCATION }]);
  const experiment = getExperiment(experimentData, CURATED_LANDING_NAV_LOCATION);
  const isVariantB = Boolean(experiment?.content?.showCuratedLandingPage);

  /**
   * if we opened this menu with the MobileNavBtn we should focus on the first
   * element in the menu ie our else case. If we got here by closing a sub menu,
   * we want to return focus to the button that opened the sub menu
   */
  useEffect(() => {
    if (menuIsSelected) {
      if (curFocusRef?.current) {
        curFocusRef.current.focus();
      } else {
        focusManager.focusFirst();
      }
    }
  }, [menuIsSelected]);

  return (
    <div ref={wrapperRef}>
      <div className={CLOSE_BTN_WRAPPER_CLASS}>
        <IconButton
          Icon={<Close />}
          ariaLabel={closeMenuLabel}
          background="dark"
          className={MOBILE_MENU_CLOSE_BTN}
          disabled={!isMobileNavDrawerOpen}
          onClick={closeMobileMenu}
          onKeyDown={keyDownHandler}
          size="small"
        />
      </div>
      {isLoggedIn && <MobileAccountMenu />}
      <div className={MAIN_MENU_WRAPPER}>
        {mobileMainMenuItems?.map(data => {
          const mainMenuLink = getMainMenuLink(data);
          const effectiveMenuLink =
            isVariantB &&
            mainMenuLink?.url &&
            CURATED_LANDING_IN_SCOPE_PATHS.has(mainMenuLink?.path?.[0])
              ? { ...mainMenuLink, children: [] }
              : mainMenuLink;
          return (
            <MobileShoppingMenuLink
              key={effectiveMenuLink?.label}
              mainMenuLink={effectiveMenuLink}
              openSubMenuClosure={linkRef =>
                openSubMenu(
                  effectiveMenuLink?.children || [],
                  allProductMsg,
                  effectiveMenuLink?.label,
                  linkRef,
                  effectiveMenuLink?.url,
                )
              }
            />
          );
        })}
      </div>
      <BrandMenu brandListWrapperClass={BRAND_MENU_WRAPPER_CLASS} displayTitles />
      <MobileUtilityMenu isLoggedIn={isLoggedIn} />
    </div>
  );
};

BaseMobileMenu.propTypes = {
  closeMobileMenu: func,
  menuIsSelected: bool,
  isMobileNavDrawerOpen: bool,
};

export default BaseMobileMenu;
